import React, { useMemo, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { ThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { resolveSsoRoute } from './ssoRouting';
function getAuthErrorAlert() {
  const searchParams = new URLSearchParams(window.location.search || '');
  const hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
  const error = searchParams.get('error') || hashParams.get('error');
  const errorDescription = searchParams.get('error_description') || hashParams.get('error_description');
  if (!error && !errorDescription) return null;
  const normalizedDescription = String(errorDescription || '').toLowerCase();
  if (error === 'invalid_request' && (normalizedDescription.includes('connection is not enabled') || normalizedDescription.includes('connection was not found'))) {
    return 'Your company has not enabled single sign-on (SSO) for this application. Please enter your SKYPAD username instead.';
  }
  return errorDescription || error || 'Authentication failed. Please try again.';
}

const theme = createMuiTheme({
  palette: {
    primary: { main: '#54B948' },
  },
  typography: {
    fontFamily: '"Segoe UI", Arial, sans-serif',
    fontSize: 12,
    body2: { fontSize: 13 },
  },
  overrides: {
    MuiOutlinedInput: {
      input: { padding: '16.5px 14px', fontSize: 13 },
    },
    MuiInputLabel: { root: { fontSize: 13 }, outlined: { transform: 'translate(14px, 17px) scale(1)' } },
  },
});

function LoginEntryPage({ onSubmit }) {
  const routeHint = useMemo(() => resolveSsoRoute(), []);
  const [identifier, setIdentifier] = useState(
    routeHint && routeHint.loginHint ? routeHint.loginHint : ''
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const authErrorMessage = useMemo(() => getAuthErrorAlert(), []);

  // Whether the URL alone carries enough to start a login with no typed
  // identifier. The client resolves no tenants of its own, so the only link
  // that qualifies is an explicit ?connection= / ?organization=, which
  // getSsoLoginOptions passes straight through to Auth0 — and which is also
  // what makes Home.js's getSsoLoginOptions() fallback reachable. Same
  // predicate as react-store-master and react-enigma-skypad3.0.
  const hasRouteSso = Boolean(routeHint.explicitConnection || routeHint.explicitOrganization);

  // No pre-submit "SSO not enabled" gate for a well-formed email: the live
  // config no longer carries users[] (removed by the 2026-09 security fix),
  // so the client can never rule out an individual allow-list entry — only
  // /api/auth/check-sso (via Home.js's handleLogin) can say "unsupported" now.
  const isInvalidEmail = identifier.includes('@') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = identifier.trim();
    if (!trimmed && !hasRouteSso) {
      setError('Enter your username or email to continue.');
      return;
    }
    if (isInvalidEmail) return;
    setError('');
    setLoading(true);
    try {
      await onSubmit(trimmed);
    } catch (err) {
      setError((err && err.message) ? err.message : "We couldn't start login. Please try again.");
    }
    setLoading(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: '100vh', backgroundColor: '#fff' }}
      >
        <Box style={{ padding: '16px', width: '100%', maxWidth: 360 }}>
          <Card
            component="form"
            onSubmit={handleSubmit}
            style={{
              padding: '56px 32px',
              borderRadius: 8,
              border: '1px solid #e0e0e0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              backgroundColor: '#fff',
            }}
          >
            <Box style={{ textAlign: 'center', marginBottom: 28 }}>
              <img
                src="https://red.skypad.biz/qlikview/images/SKYPAD%20Logo.png"
                alt="SKYPAD"
                style={{ width: '100%', height: 'auto' }}
              />
            </Box>
            <Box style={{ marginBottom: 20, textAlign: 'left' }}>
              <Typography variant="body2" style={{ color: '#333', lineHeight: 1.7 }}>
                <strong>SSO clients:</strong> Enter your company email address
              </Typography>
              <Typography variant="body2" style={{ color: '#333', lineHeight: 1.7 }}>
                <strong>Non-SSO clients:</strong> Enter your SKYPAD username
              </Typography>
            </Box>
            {authErrorMessage && (
              <>
                <Box
                  display="flex"
                  alignItems="flex-start"
                  style={{
                    backgroundColor: '#fff3e0',
                    borderRadius: 4,
                    padding: '10px 16px',
                    marginBottom: 16,
                    color: '#7c4a00',
                  }}
                >
                  <ReportProblemOutlinedIcon style={{ color: '#ed6c02', marginRight: 12, marginTop: 2, fontSize: 22 }} />
                  <Typography variant="body2" style={{ color: '#7c4a00', lineHeight: 1.6, paddingTop: 2, textAlign: 'left' }}>
                    {authErrorMessage}
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => window.location.replace('/')}
                  style={{
                    borderRadius: 6,
                    borderColor: '#54b948',
                    color: '#54b948',
                    fontWeight: 600,
                    textTransform: 'none',
                  }}
                >
                  Back
                </Button>
              </>
            )}
            {!authErrorMessage && isInvalidEmail && (
              <Box display="flex" alignItems="flex-start" style={{ backgroundColor: '#fdecea', borderRadius: 4, padding: '6px 16px', marginBottom: 16, color: '#611a15' }}>
                <ErrorOutlineIcon style={{ color: '#f44336', marginRight: 12, marginTop: 3, fontSize: 22 }} />
                <Typography variant="body2" style={{ color: '#611a15', lineHeight: 1.6, paddingTop: 3, textAlign: 'left' }}>Enter a valid email address.</Typography>
              </Box>
            )}
            {!authErrorMessage && !isInvalidEmail && error && (
              <Box display="flex" alignItems="flex-start" style={{ backgroundColor: '#fdecea', borderRadius: 4, padding: '6px 16px', marginBottom: 16, color: '#611a15' }}>
                <ErrorOutlineIcon style={{ color: '#f44336', marginRight: 12, marginTop: 3, fontSize: 22 }} />
                <Typography variant="body2" style={{ color: '#611a15', lineHeight: 1.6, paddingTop: 3, textAlign: 'left' }}>{error}</Typography>
              </Box>
            )}
            {!authErrorMessage && (
              <>
                <Box style={{ marginBottom: 20 }}>
                  <TextField
                    autoFocus
                    fullWidth
                    label="Company email or username*"
                    variant="outlined"
                    value={identifier}
                    onChange={(e) => {
                      setIdentifier(e.target.value);
                      if (error) setError('');
                    }}
                    disabled={loading}
                  />
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={loading || isInvalidEmail}
                  disableElevation
                  style={{
                    padding: '11px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 6,
                    color: '#fff',
                  }}
                >
                  {loading ? 'Continuing...' : 'Continue'}
                </Button>
              </>
            )}
          </Card>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default LoginEntryPage;
